import { Link } from '@tanstack/react-router';
import { CircleHelp, X } from 'lucide-react';
import { useState } from 'react';
import { buttonClass } from '../components/ui.js';
import { useAppMeta } from '../lib/meta.js';

/**
 * A welcome at the top of the home screen for a new deploy's first two weeks, pointing at
 * Help. Closing it is remembered in this browser, for this organization.
 */

const WELCOME_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

function dismissedKey(orgId: string): string {
  return `databink.welcome.${orgId}`;
}

/** Closed in this page's life, for when the browser keeps nothing. */
const closedHere = new Set<string>();

function wasDismissed(orgId: string): boolean {
  if (closedHere.has(orgId)) return true;
  try {
    return window.localStorage.getItem(dismissedKey(orgId)) !== null;
  } catch {
    // A browser that keeps nothing would show the welcome on every visit: leave it out.
    return true;
  }
}

/** Whether an organization set up at `createdAt` is still new at `now`. */
export function isNewDeploy(createdAt: string, now: number = Date.now()): boolean {
  const started = Date.parse(createdAt);
  return Number.isFinite(started) && now - started < WELCOME_DAYS * DAY_MS;
}

export function WelcomeCard() {
  const meta = useAppMeta();
  const org = meta.organization;
  const [hidden, setHidden] = useState(() => wasDismissed(org.id));
  if (hidden || meta.copy.neutralized || !isNewDeploy(org.created_at)) return null;
  const close = () => {
    setHidden(true);
    closedHere.add(org.id);
    try {
      window.localStorage.setItem(dismissedKey(org.id), new Date().toISOString());
    } catch {
      // Not remembered: it shows again on the next visit, not before.
    }
  };
  return (
    <section
      aria-labelledby="welcome-title"
      className="relative mb-5 rounded-lg border border-brand-muted bg-brand-soft px-4 py-4 pr-12 sm:px-5"
    >
      <h2 id="welcome-title" className="text-base font-semibold text-slate-900">
        Welcome to {meta.branding.product_name}
      </h2>
      <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-700">
        Help, in the menu, has a short guide to each screen. Start with finding your way around; it
        takes a couple of minutes.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/help/$guide"
          params={{ guide: 'getting-started' }}
          className={buttonClass('primary')}
        >
          <CircleHelp className="size-4" aria-hidden />
          Finding your way around
        </Link>
        <Link to="/help" className={buttonClass('secondary')}>
          All the guides
        </Link>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Close the welcome"
        className="absolute top-3 right-3 rounded-md p-1.5 text-slate-500 hover:bg-white/60 hover:text-slate-900"
      >
        <X className="size-4" aria-hidden />
      </button>
    </section>
  );
}
